import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ParentDisclosureService } from '../../services/parent-disclosure.service';
import { FamilyKidsService } from '../../../parent-family/services/family-kids.service';
import { ParentEmptyStateComponent } from '../../../../shared/components/ui/parent-empty-state/parent-empty-state.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { InfoBannerComponent } from '../../../../shared/components/ui/info-banner/info-banner.component';
import {
  formatDisclosureExpiry,
  formatDisclosurePartnerRef,
  resolveDisclosureScopeLabel,
} from '../../helpers/disclosure-scope.helper';
import { DisclosureRequest } from '../../models/disclosure.model';
import { getValvurApiError } from '../../../../core/errors/parse-valvur-api-error';
import { StepUpPasswordService } from '../../services/step-up-password.service';

@Component({
  selector: 'app-parent-disclosure-approvals',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    TranslatePipe,
    ParentEmptyStateComponent,
    ParentButtonComponent,
    InfoBannerComponent,
  ],
  templateUrl: './parent-disclosure-approvals.component.html',
  styleUrl: './parent-disclosure-approvals.component.scss',
})
export class ParentDisclosureApprovalsComponent {
  #disclosure = inject(ParentDisclosureService);
  #familyKids = inject(FamilyKidsService);
  #translate = inject(TranslateService);
  #router = inject(Router);
  #stepUp = inject(StepUpPasswordService);

  readonly actingRequestId = signal<string | null>(null);
  readonly pageError = signal<string | null>(null);

  readonly pending = computed(() => this.#disclosure.pending());
  readonly isLoading = computed(
    () => this.#disclosure.pendingQuery.isPending(),
  );
  readonly showLoadError = computed(
    () => this.#disclosure.pendingQuery.isError(),
  );

  kidName(subjectUserId: string | undefined): string {
    if (!subjectUserId) {
      return '';
    }
    const child = this.#familyKids.childById(subjectUserId);
    return child ? this.#familyKids.displayName(child) : '';
  }

  partnerLabel(partnerRef: string | undefined): string {
    return formatDisclosurePartnerRef(partnerRef ?? '');
  }

  scopeLabel(scopeId: string): string {
    return resolveDisclosureScopeLabel(scopeId, (key) =>
      this.#translate.instant(key),
    );
  }

  expiryDate(request: DisclosureRequest): Date | null {
    return formatDisclosureExpiry(request.expiresAt);
  }

  isActing(requestId: string): boolean {
    return this.actingRequestId() === requestId;
  }

  async refresh(): Promise<void> {
    this.pageError.set(null);
    await this.#disclosure.reloadPending();
  }

  goBack(): void {
    void this.#router.navigate(['/identity']);
  }

  async approve(request: DisclosureRequest): Promise<void> {
    if (!request.id) {
      return;
    }
    this.pageError.set(null);

    const stepUp = await this.#stepUp.prompt({
      titleKey: 'app.identity.stepUp.approveTitle',
      bodyKey: 'app.identity.stepUp.approveBody',
      confirmKey: 'app.identity.disclosure.actions.approve',
    });
    if (!stepUp) {
      return;
    }

    this.actingRequestId.set(request.id);
    try {
      await this.#disclosure.approveMutation.mutateAsync({
        requestId: request.id,
        password: stepUp.password,
        totpCode: stepUp.totpCode,
      });
    } catch (error) {
      this.pageError.set(
        getValvurApiError(error)?.message ??
          this.#translate.instant('app.identity.disclosure.page.actionError'),
      );
    } finally {
      this.actingRequestId.set(null);
    }
  }

  async deny(request: DisclosureRequest): Promise<void> {
    if (!request.id) {
      return;
    }
    this.pageError.set(null);
    this.actingRequestId.set(request.id);
    try {
      await this.#disclosure.denyMutation.mutateAsync(request.id);
    } catch (error) {
      this.pageError.set(
        getValvurApiError(error)?.message ??
          this.#translate.instant('app.identity.disclosure.page.actionError'),
      );
    } finally {
      this.actingRequestId.set(null);
    }
  }
}
